import { logServiceEvent } from "../../helper/logger";

export const isNationalForGenre = (localRsnChannel: any, packageKey: string, dma: string, nielsenDma?: string, genreNationalMapping?: any): boolean => {
  try {
    if (!genreNationalMapping || !Array.isArray(genreNationalMapping)) {
      return false;
    }
    const isLocalChannelForNational = genreNationalMapping.find?.(channel => {
      try {
        return (channel?.channel === localRsnChannel?.network || channel?.channel === localRsnChannel?.name);
      } catch {
        return false;
      }
    });
    if (!isLocalChannelForNational) {
      return false;
    }
    const isLocalBelongToNational = isLocalChannelForNational ? ![undefined, null, ""].includes(isLocalChannelForNational?.[packageKey]) : false;
    const dmaValue = dma?.toString?.() || nielsenDma?.toString?.();
    const isNationalChannelInDma = isLocalBelongToNational && isLocalChannelForNational?.[packageKey]?.split?.(",")?.includes?.(dmaValue);
    return !!isNationalChannelInDma;
  } catch {
    // fallback: return false if error occurs
    return false;
  }
};

export const handleLocalRsnChannelFilter = (
  localRsnChannels: any,
  genreLocalChannelMapping: any,
  packageKey: string,
  dma: string,
  nielsenDma?: string,
  genreNationalMapping?: any
) => {
  try {
    if (!Array.isArray(localRsnChannels)) {
      return [];
    }
    const filteredChannels = localRsnChannels?.filter(localRsnChannel => {
      try {
        // RSN channels filter for MyHomeTeam genre
        if (packageKey === "myhometeam") {
          const isMyHomeTeamEligible = localRsnChannel?.packageNames?.includes?.("MYHOMETEAM");
          const isMyHomeTeamHasTeams = localRsnChannel?.mlb || localRsnChannel?.nhl || localRsnChannel?.nba || localRsnChannel?.wnba;
          return isMyHomeTeamEligible && isMyHomeTeamHasTeams;
        }
        // Local channels filter for rest of the genres
        const isLocalChannel = genreLocalChannelMapping?.find?.(channel => (channel?.channel === localRsnChannel?.network || channel?.channel === localRsnChannel?.name));

        const isLocalBelongToGenre = isLocalChannel ? ![undefined, null, ""]?.includes?.(isLocalChannel?.[packageKey]) : false;
        const isLocalChannelInDma =
          isLocalBelongToGenre &&
          (isLocalChannel?.[packageKey]?.toLowerCase() === "all" || isLocalChannel?.[packageKey]?.split?.(",").includes?.(dma?.toString?.() || nielsenDma?.toString?.()));

        const isNational = isNationalForGenre(localRsnChannel, packageKey, dma, nielsenDma, genreNationalMapping);
        if (isNational && !(isLocalChannel && isLocalBelongToGenre && isLocalChannelInDma)) {
          localRsnChannel.isNational = isNational;
        }
        return (isLocalChannel && isLocalBelongToGenre && isLocalChannelInDma) || isNational;
      } catch {
        // fallback: include the channel if error occurs
        return true;
      }
    });

    return filteredChannels ?? [];
  } catch {
    // fallback: return empty array if error occurs
    return [];
  }
};

// Generated by Copilot- Start
const buildDmaSet = (value: any): Set<string> | null => {
  if (!value || typeof value !== "string") {
    return null;
  }
  const entries = value
    .split?.(",")
    ?.map?.(dma => dma?.trim?.())
    ?.filter?.(dma => !!dma);
  return entries && entries.length ? new Set(entries) : null;
};

const hasLocalChannelForNational = (locals: any[], baseChannel: string): boolean => (Array.isArray(locals) ?
  locals.some(localChannel => {
    const matchesByNetwork = baseChannel === localChannel?.network && !localChannel?.isNational;
    const matchesByLabel = baseChannel === localChannel?.channelNameLabel && localChannel?.categoryId === "Local";
    return matchesByNetwork || matchesByLabel;
  }) :
  false);

const pruneLocalChannels = (locals: any[], baseChannel: string, packageKey: string): void => {
  if (!Array.isArray(locals)) {
    return;
  }
  for (let idx = locals.length - 1; idx >= 0; idx -= 1) {
    const localChannel = locals[idx];
    const isSameChannel = baseChannel === localChannel?.network || baseChannel === localChannel?.channelNameLabel;
    if (!isSameChannel) {
      continue;
    }

    if (localChannel?.categoryId === "National" && Array.isArray(localChannel?.packageListSystem)) {
      localChannel.packageListSystem = localChannel.packageListSystem.filter((pkg: string) => !pkg?.includes?.(packageKey?.toUpperCase?.()));
      continue;
    }

    if (localChannel?.isNational) {
      locals.splice(idx, 1);
    }
  }
};

const pruneNationalChannelsByAlias = (
  channels: any[],
  aliasSet: Set<string>,
  baseChannel: string,
  hasLocalChannel: boolean,
  isDmaEligible: boolean
): any[] => {
  if (!Array.isArray(channels) || !aliasSet.size) {
    return channels;
  }

  const nextChannels: any[] = [];
  let primaryAssigned = false;
  const canNormalizeAliases = aliasSet.size > 1;

  for (const nationalChannel of channels) {
    const channelName = nationalChannel?.channelNameLabel;

    if (!aliasSet.has(channelName)) {
      if (channelName === baseChannel) {
        if (primaryAssigned) {
          continue;
        }
        primaryAssigned = true;
      }
      nextChannels.push(nationalChannel);
      continue;
    }

    if (hasLocalChannel || !isDmaEligible) {
      continue;
    }

    if (!canNormalizeAliases) {
      nextChannels.push(nationalChannel);
      continue;
    }

    if (primaryAssigned) {
      continue;
    }

    primaryAssigned = true;
    nextChannels.push(
      channelName === baseChannel ? nationalChannel : {
        ...nationalChannel,
        channelNameLabel: baseChannel
      }
    );
  }

  return nextChannels;
};

const hasAnyNationalFlags = (flags: any): boolean => !!flags && typeof flags === "object" && Object.keys(flags).length > 0;

const hasLocalChannelForChlup = (channels: any[], baseChannel: string): boolean => {
  if (!Array.isArray(channels)) return false;
  // Detect LOCAL via the structural flag when present (set by buildLocalRsnChannel),
  // falling back to the scalar `category` for any channel that predates the flag.
  // Both resolve identically in Stage 1 (every built LOCAL channel carries both).
  return channels.some(
    (channel) => (channel?.isLocal || channel?.category === "LOCAL") && channel?.name === baseChannel && !hasAnyNationalFlags(channel?.isNational)
  );
};

const pruneChlupChannels = (
  channels: any[],
  aliasSet: Set<string>,
  baseChannel: string,
  {
    hasLocalChannelInDma,
    isNationalChannelInDma,
    categoryKeyLower,
  }: { hasLocalChannelInDma: boolean; isNationalChannelInDma: boolean; categoryKeyLower: string }
): any[] => {
  if (!Array.isArray(channels) || !aliasSet.size) {
    return channels;
  }

  const nextChannels: any[] = [];
  let aliasCount = 0;
  let aliasAssigned = false;
  const canNormalizeAliases = aliasSet.size > 1;

  for (const channel of channels) {
    const channelName = channel?.name;

    if (!aliasSet.has(channelName)) {
      nextChannels.push(channel);
      continue;
    }

    aliasCount += 1;

    if (hasLocalChannelInDma || !isNationalChannelInDma) {
      continue;
    }

    if (!canNormalizeAliases) {
      nextChannels.push(channel);
      continue;
    }

    if (aliasAssigned) {
      continue;
    }

    aliasAssigned = true;
    nextChannels.push(channelName === baseChannel ? channel : {
      ...channel,
      name: baseChannel
    });
  }

  if (!canNormalizeAliases || !aliasCount) {
    return nextChannels;
  }

  return nextChannels.filter(channel => {
    if (!hasAnyNationalFlags(channel?.isNational)) {
      return true;
    }
    return !channel?.isNational?.[categoryKeyLower];
  });
};
// Generated by Copilot- End

export const handleNationalChannelDMAFilter = ({ filteredLocalChannels, nationalChannels, currentDma, nationalMapping, packageKey }) => {
  let filteredNationalChannels = Array.isArray(nationalChannels) ? [...nationalChannels] : [];
  const updatedLocalChannels = Array.isArray(filteredLocalChannels) ? [...filteredLocalChannels] : [];

  try {
    if (!Array.isArray(nationalMapping)) {
      return {
        filteredNationalChannels,
        updatedLocalChannels
      };
    }

    const currentDmaStr = currentDma?.toString?.() ?? "";

    for (const nationalMap of nationalMapping) {
      // Generated by Copilot- Start
      try {
        const aliasList = nationalMap?.channelAliases;
        const baseChannel = nationalMap?.channel;
        if (!baseChannel || !Array.isArray(aliasList) || !aliasList.length) {
          continue;
        }

        const aliasSet = new Set(aliasList.filter(Boolean));
        if (!aliasSet.size) {
          continue;
        }

        const dmaSet = buildDmaSet(nationalMap?.[packageKey]);
        const isNationalChannelInDma = dmaSet ? dmaSet.has(currentDmaStr) : false;
        const isLocalChannelInDma = hasLocalChannelForNational(updatedLocalChannels, baseChannel);

        if (isLocalChannelInDma || isNationalChannelInDma) {
          pruneLocalChannels(updatedLocalChannels, baseChannel, packageKey);
        }

        filteredNationalChannels = pruneNationalChannelsByAlias(
          filteredNationalChannels,
          aliasSet,
          baseChannel,
          isLocalChannelInDma,
          isNationalChannelInDma
        );
      } catch {
        continue;
      }
      // Generated by Copilot- End
    }
  } catch {
    return {
      filteredNationalChannels,
      updatedLocalChannels
    };
  }

  return {
    filteredNationalChannels,
    updatedLocalChannels
  };
};

export const handleNationalLocalDmaFilterChlup = ({ nationalChannels, nationalMapping, packageKey, categoryKey = packageKey, currentDma }: {
  nationalChannels: any[];
  nationalMapping: any[];
  packageKey: string;
  categoryKey?: string;
  currentDma: string | number;
}) => {
  let filteredDmaEligibleChannels = Array.isArray(nationalChannels) ? [...nationalChannels] : [];
  try {
    if (!Array.isArray(nationalMapping)) {
      return filteredDmaEligibleChannels;
    }

    const packageKeyLower = packageKey?.toLowerCase?.() ?? "";
    const categoryKeyLower = categoryKey?.toLowerCase?.() ?? "";
    const currentDmaStr = currentDma?.toString?.() ?? "";

    for (const nationalMap of nationalMapping) {
      try {
        // Generated by Copilot- Start
        const aliasList = nationalMap?.channelAliases;
        const baseChannel = nationalMap?.channel;
        if (!baseChannel || !Array.isArray(aliasList) || !aliasList.length) {
          continue;
        }

        const aliasSet = new Set(aliasList.filter(Boolean));
        if (!aliasSet.size) {
          continue;
        }

        const dmaSet = buildDmaSet(nationalMap?.[packageKeyLower]);
        const isNationalChannelInDma = dmaSet ? dmaSet.has(currentDmaStr) : false;
        const hasLocalChannelInDma = hasLocalChannelForChlup(nationalChannels, baseChannel);

        filteredDmaEligibleChannels = pruneChlupChannels(filteredDmaEligibleChannels, aliasSet, baseChannel, {
          categoryKeyLower,
          hasLocalChannelInDma,
          isNationalChannelInDma
        });
        // Generated by Copilot- End
      } catch (err) {
        // continue to next nationalMap on error
        console.error("handleNationalLocalDmaFilterChlup error", err);
        continue;
      }
    }
  } catch (err) {
    // fallback: return whatever is available
    console.error("handleNationalLocalDmaFilterChlup error", err);
    return filteredDmaEligibleChannels;
  }
  return filteredDmaEligibleChannels;
};

// Generated by Copilot- Start
/**
 * Deduplication Utilities for Local/National Channel Filtering
 * Builds a lookup map for local/RSN channels to identify duplicates with national channels
 * Maps base channel names to their local instances and creates alias-to-base mappings
 * OPTIMIZATION: Only includes aliases for channels that actually exist in local/RSN data
 *
 * @param localChannels - Array of local channels from zipcode API
 * @param rsnChannels - Array of RSN channels from zipcode API
 * @param genreNationalMapping - Mapping array with channel aliases from genreNationals config
 * @returns Object with baseChannelMap (base name -> local channel) and aliasToBaseMap (alias -> base name)
 */
export const buildLocalChannelLookup = (
  localChannels: any[],
  rsnChannels: any[],
  genreNationalMapping?: any[]
): {
  baseChannelMap: Map<string, any>;
  aliasToBaseMap: Map<string, string>;
} => {
  const baseChannelMap = new Map<string, any>();
  const aliasToBaseMap = new Map<string, string>();

  try {
    // Exit early if no mapping data
    if (!genreNationalMapping || !Array.isArray(genreNationalMapping)) {
      return {
        baseChannelMap,
        aliasToBaseMap
      };
    }

    const allLocalRsn = [...(localChannels || []), ...(rsnChannels || [])];
    if (!allLocalRsn.length) {
      return {
        baseChannelMap,
        aliasToBaseMap
      };
    }

    // Build lookup: use display name (network/name/callSign) as used in UI
    const localChannelNames = new Set<string>();
    allLocalRsn.forEach(channel => {
      const displayName = channel?.network || channel?.name || channel?.callSign;
      if (displayName) {
        baseChannelMap.set(displayName, channel);
        localChannelNames.add(displayName);
      }
    });

    // OPTIMIZATION: Build alias-to-base mapping ONLY for channels that exist in local/RSN data
    // This prevents unnecessary filtering iterations for channels not in the DMA
    let includedMappings = 0;

    genreNationalMapping.forEach(mapping => {
      const baseChannel = mapping?.channel;
      const aliases = mapping?.channelAliases;

      if (!baseChannel || !Array.isArray(aliases) || !aliases.length) {
        return;
      }

      // OPTIMIZATION: Only include this mapping if the base channel exists in local/RSN data
      if (!localChannelNames.has(baseChannel)) {
        return; // Skip this mapping - no local channel present
      }

      // Include aliases for this channel
      aliases.forEach(alias => {
        if (alias) {
          aliasToBaseMap.set(alias, baseChannel);
        }
      });
      includedMappings += 1;
    });

    logServiceEvent("Channels", "buildLocalChannelLookup", {
      localRsnCount: baseChannelMap.size,
      aliasCount: aliasToBaseMap.size,
      mappingsUsed: includedMappings
    });
  } catch (error) {
    console.error("buildLocalChannelLookup error:", error);
  }

  return {
    baseChannelMap,
    aliasToBaseMap
  };
};

/**
 * Determines if a national channel should be removed or have signature packages stripped
 * Based on local channel presence and DMA eligibility for genre packs
 *
 * @param nationalChannel - The national channel to check (uses label/displayText/channelNameLabel)
 * @param localChannelLookup - Lookup maps from buildLocalChannelLookup
 * @param dmaData - Current DMA information
 * @param genreLocalMapping - Genre-to-DMA eligibility mapping
 * @param genreNationalMapping - National channel aliases mapping
 * @param packageSummaries - Package summaries for genre pack identification (optional, for non-genre variants)
 * @returns { remove: boolean, stripSignaturePackages: boolean }
 */
export const shouldRemoveNationalChannel = (
  nationalChannel: any,
  localChannelLookup: { baseChannelMap: Map<string, any>; aliasToBaseMap: Map<string, string> },
  dmaData: { dma?: string; nielsenDma?: string } | null,
  genreLocalMapping?: any[],
  genreNationalMapping?: any[],
  packageSummaries?: any
): { remove: boolean; stripSignaturePackages: boolean } => {
  try {
    // Exit early if no mapping data
    if (!genreNationalMapping || !Array.isArray(genreNationalMapping)) {
      return {
        remove: false,
        stripSignaturePackages: false
      };
    }

    const { baseChannelMap, aliasToBaseMap } = localChannelLookup;

    // Get national channel display name (as shown in UI)
    const nationalDisplayName = nationalChannel?.label || nationalChannel?.displayText || nationalChannel?.channelNameLabel || nationalChannel?.name;
    if (!nationalDisplayName) {
      return {
        remove: false,
        stripSignaturePackages: false
      };
    }

    // Check if this national channel is an alias
    const baseChannelName = aliasToBaseMap.get(nationalDisplayName);
    if (!baseChannelName) {
      // Not an alias, no action needed
      return {
        remove: false,
        stripSignaturePackages: false
      };
    }

    // Check if local/RSN channel exists for this base name
    const localOrRsnChannel = baseChannelMap.get(baseChannelName);
    if (!localOrRsnChannel) {
      // No local/RSN channel, keep national
      return {
        remove: false,
        stripSignaturePackages: false
      };
    }

    // Local/RSN channel exists - now check DMA eligibility for genre packs
    const dma = dmaData?.dma || "";
    const nielsenDma = dmaData?.nielsenDma;

    if (!genreLocalMapping || !Array.isArray(genreLocalMapping)) {
      // No genre mapping - remove national entirely (local/RSN covers all signature packages)
      return {
        remove: true,
        stripSignaturePackages: false
      };
    }

    // Find local/RSN channel's genre eligibility
    const localGenreMapping = genreLocalMapping.find(mapping => mapping?.channel === baseChannelName);

    if (!localGenreMapping) {
      // Local/RSN not mapped to any genre - remove national entirely
      return {
        remove: true,
        stripSignaturePackages: false
      };
    }

    // Check DMA eligibility across all genre packages
    let isEligibleForAnyGenre = false;
    const genrePackageKeys = packageSummaries ?
      Object.values(packageSummaries)
        .filter((pkg: any) => pkg?.type === "genre" || pkg?.type === "genre-mini")
        .map((pkg: any) => pkg?.queryParam) :
      Object.keys(localGenreMapping).filter(key => key !== "channel");

    for (const packageKey of genrePackageKeys) {
      const dmaList = localGenreMapping[packageKey];
      if (!dmaList || dmaList === "" || dmaList === null || dmaList === undefined) {
        continue;
      }

      // Check if DMA is eligible
      const isEligible =
        dmaList.toLowerCase() === "all" ||
        dmaList.split(",").some((d: string) => d.trim() === dma || d.trim() === nielsenDma);

      if (isEligible) {
        isEligibleForAnyGenre = true;
        break;
      }
    }

    if (isEligibleForAnyGenre) {
      // Local/RSN is eligible for genre packs - remove national entirely
      return {
        remove: true,
        stripSignaturePackages: false
      };
    }
    // Local/RSN NOT eligible for genre packs - keep national but strip signature packages
    // National should only show genre packages (avoid duplicate signature packages)
    return {
      remove: false,
      stripSignaturePackages: true
    };
  } catch (error) {
    console.error("shouldRemoveNationalChannel error:", error);
    return {
      remove: false,
      stripSignaturePackages: false
    };
  }
};

/**
 * Filters an array of channels to remove national duplicates based on local channel availability
 * Optionally strips signature packages from nationals that should only show genre packs
 *
 * @param channels - Array of channels (can be ChannelCardV2[] or normalized string[])
 * @param localChannelLookup - Lookup maps from buildLocalChannelLookup
 * @param channelMap - Channel map for denormalization (if channels are string[])
 * @param dmaData - Current DMA information
 * @param genreLocalMapping - Genre-to-DMA eligibility mapping
 * @param genreNationalMapping - National channel aliases mapping
 * @param packageSummaries - Package summaries (optional, for non-genre variants)
 * @returns Filtered channel array (maintains same type as input)
 */
export const filterDuplicateNationalChannels = (
  channels: any[],
  localChannelLookup: { baseChannelMap: Map<string, any>; aliasToBaseMap: Map<string, string> },
  channelMap: Record<string, any> | undefined,
  dmaData: { dma?: string; nielsenDma?: string } | null,
  genreLocalMapping?: any[],
  genreNationalMapping?: any[],
  packageSummaries?: any
): any[] => {
  try {
    if (!Array.isArray(channels) || !channels.length) {
      return channels;
    }

    // Exit early if no mapping data
    if (!genreNationalMapping || !Array.isArray(genreNationalMapping)) {
      return channels;
    }

    const { aliasToBaseMap, baseChannelMap } = localChannelLookup;

    // OPTIMIZATION: Skip filtering if no local channels exist
    // BUT still filter if baseChannelMap has entries (for RSN direct name matches)
    if (aliasToBaseMap.size === 0 && baseChannelMap.size === 0) {
      return channels;
    }

    // Check if channels are normalized (string[]) or denormalized (object[])
    const isNormalized = typeof channels[0] === "string";

    const filteredChannels: any[] = [];
    let removedCount = 0;
    let strippedCount = 0;
    let directMatchRemovals = 0;

    for (const channel of channels) {
      let channelObj: any;
      let channelId: string | undefined;

      if (isNormalized) {
        // Normalized - lookup in channelMap
        channelId = channel as string;
        channelObj = channelMap?.[channelId];
        if (!channelObj) {
          // Keep if not found in map
          filteredChannels.push(channel);
          continue;
        }
      } else {
        // Already denormalized
        channelObj = channel;
      }

      // Get channel display name
      const displayName = channelObj?.label || channelObj?.displayText || channelObj?.channelNameLabel || channelObj?.name;

      // OPTIMIZATION 1: Check if there's a direct name match with any local/RSN channel
      // This handles RSN channels that don't have aliases in genreNationalMapping
      const directMatch = baseChannelMap.get(displayName);
      if (directMatch) {
        // Found a local/RSN channel with exact same name - remove this national
        removedCount += 1;
        directMatchRemovals += 1;
        continue;
      }

      // OPTIMIZATION 2: Quick check if this channel is in the alias map
      const baseChannelName = aliasToBaseMap.get(displayName);

      if (!baseChannelName) {
        // Not an alias and no direct match, keep channel immediately
        filteredChannels.push(channel);
        continue;
      }

      // This is an alias - now do the full check
      const { remove, stripSignaturePackages } = shouldRemoveNationalChannel(
        channelObj,
        localChannelLookup,
        dmaData,
        genreLocalMapping,
        genreNationalMapping,
        packageSummaries
      );

      if (remove) {
        // Skip this channel (remove from list)
        removedCount += 1;
        continue;
      }

      if (stripSignaturePackages && !isNormalized) {
        // Strip signature packages in-place (only for denormalized objects)
        const strippedChannel = {
          ...channelObj,
          signaturePackages: [],
          hasIncludedPackages: (channelObj?.genrePackages?.length || 0) > 0
        };
        filteredChannels.push(strippedChannel);
        strippedCount += 1;
      } else {
        // Keep as-is
        filteredChannels.push(channel);
      }
    }

    // Only log if there were actual removals or stripping
    if (removedCount > 0 || strippedCount > 0) {
      logServiceEvent("Channels", "filterDuplicateNationalChannels", {
        total: channels.length,
        removed: removedCount,
        directMatches: directMatchRemovals,
        stripped: strippedCount,
        final: filteredChannels.length
      });
    }

    return filteredChannels;
  } catch (error) {
    console.error("filterDuplicateNationalChannels error:", error);
    return channels;
  }
};
// Generated by Copilot- End

// Generated by Copilot- Start - 2026-04-23 09:00:00
/**
 * Checks if a local channel should be excluded from a signature package for a given DMA.
 * Reads from `signatureExcludedLocalChannels` CMS config (Storyblok block: signatureExcludedLocalChannels).
 *
 * Hybrid lookup (scales to 100+ DMAs):
 *   1. Blanket — DMA in `blanketDmas` → excluded from ALL signature pkgs for that channel
 *   2. Narrow  — DMA-specific entry in `perDmaOverrides` with `excludeFromPackages` listing pkgs to hide
 *
 * Authoring shape:
 *   { channel: "Telemundo",
 *     blanketDmas: "820,635,819,801",
 *     perDmaOverrides: [
 *       { dma: "908", excludeFromPackages: "ATT_TV_ENTERTAINMENT,ATT_TV_CHOICE,ATT_TV_ULTIMATE" }
 *     ] }
 *
 * @param channelNetwork - Channel's network (matches xapi response e.g. "Telemundo")
 * @param dma            - User's DMA code
 * @param signatureExcludedLocalChannels - Array from Storyblok CMS config
 * @param packageId      - Optional signature package ID. Required for narrow-override resolution.
 * @returns true if the channel should be hidden from the (optionally specified) signature package
 */
export const isLocalChannelExcludedFromSignature = (
  channelNetwork: string,
  dma: string,
  signatureExcludedLocalChannels?: any[],
  packageId?: string
): boolean => {
  try {
    if (!Array.isArray(signatureExcludedLocalChannels) || !channelNetwork || !dma) {
      return false;
    }

    const entry = signatureExcludedLocalChannels.find(e => e?.channel === channelNetwork);
    if (!entry) {
      return false;
    }

    const dmaStr = dma.toString().trim();

    // 1. Blanket fast-path — `blanketDmas` is a Storyblok textarea: CSV / newlines / mixed
    if (entry.blanketDmas) {
      const list = Array.isArray(entry.blanketDmas) ?
        entry.blanketDmas :
        String(entry.blanketDmas).split(/[,\s]+/);
      if (list.map((d: any) => d?.toString().trim()).filter(Boolean).includes(dmaStr)) {
        return true;
      }
    }

    // 2. Per-DMA narrow override
    const override = Array.isArray(entry.perDmaOverrides) ?
      entry.perDmaOverrides.find((o: any) => o?.dma?.toString().trim() === dmaStr) :
      undefined;
    if (!override?.excludeFromPackages) {
      return false;
    }

    if (!packageId) {
      return false;
    } // partial exclusion — let per-package consumers re-check
    const excludedPkgs = override.excludeFromPackages.split(",").map((p: string) => p.trim());
    return excludedPkgs.includes(packageId);
  } catch {
    return false;
  }
};

/**
 * Returns the list of signature packages a local channel should appear in,
 * after removing any packages excluded by the CMS config.
 */
export const getSignaturePackagesForLocalChannel = (
  channelNetwork: string,
  dma: string,
  allSignaturePackages: string[],
  signatureExcludedLocalChannels?: any[]
): string[] => {
  try {
    if (!Array.isArray(allSignaturePackages)) {
      return allSignaturePackages;
    }
    if (!Array.isArray(signatureExcludedLocalChannels) || signatureExcludedLocalChannels.length === 0) {
      return allSignaturePackages;
    }
    return allSignaturePackages.filter(
      pkgId => !isLocalChannelExcludedFromSignature(channelNetwork, dma, signatureExcludedLocalChannels, pkgId)
    );
  } catch {
    return allSignaturePackages;
  }
};

/**
 * Filters out local channels that are excluded from a signature package for the given DMA.
 * Pass `packageId` to apply per-package narrow rules; omit to filter only on blanket exclusion.
 */
export const filterSignatureExcludedLocalChannels = (
  localChannels: any[],
  dma: string,
  signatureExcludedLocalChannels?: any[],
  packageId?: string
): any[] => {
  try {
    if (!Array.isArray(localChannels)) {
      return localChannels;
    }
    if (!Array.isArray(signatureExcludedLocalChannels) || signatureExcludedLocalChannels.length === 0) {
      return localChannels;
    }
    return localChannels.filter(channel => {
      const network = channel?.network || channel?.name || channel?.channelNameLabel || channel?.label;
      return !isLocalChannelExcludedFromSignature(network, dma, signatureExcludedLocalChannels, packageId);
    });
  } catch {
    return localChannels;
  }
};
// Generated by Copilot- End - 2026-04-23 09:00:00
